import { all, put, takeEvery, takeLatest } from "@redux-saga/core/effects";
import {
  submitSupportRequest as submitSupportRequestApi,
  getSupportRequests,
  getSupportRequestComments,
  addSupportRequestComment as addSupportRequestCommentApi,
  closeSupportRequest as closeSupportRequestApi,
  reopenSupportRequest as reopenSupportRequestApi
} from "../../Services/api";
import { setErrorStatus, setSuccessStatus } from "../status/action";
import store from "../store";

/**
 * @module SupportSaga
 */

export const actionTypes = {
  SUBMIT_SUPPORT_REQUEST: "SUBMIT_SUPPORT_REQUEST",
  FETCH_SUPPORT_REQUESTS: "FETCH_SUPPORT_REQUESTS",
  FETCH_SUPPORT_REQUEST_COMMENTS: "FETCH_SUPPORT_REQUEST_COMMENTS",
  ADD_SUPPORT_REQUEST_COMMENT: "ADD_SUPPORT_REQUEST_COMMENT",
  CLOSE_SUPPORT_REQUEST: "CLOSE_SUPPORT_REQUEST",
  REOPEN_SUPPORT_REQUEST: "REOPEN_SUPPORT_REQUEST"
};

function* setSubmitting(bool) {
  yield put({ type: "SET_SUPPORT_SUBMITTING", payload: { submitting: bool } });
}

function* setRequestsLoading(bool) {
  yield put({ type: "SET_SUPPORT_REQUESTS_LOADING", payload: { loading: bool } });
}

function* setCommentsLoading(workItemId, bool) {
  yield put({
    type: "SET_SUPPORT_COMMENTS_LOADING",
    payload: { workItemId, loading: bool }
  });
}

function* fetchSupportRequestsWorker() {
  try {
    yield setRequestsLoading(true);
    const accessToken = store.getState().auth.data.accessToken;
    const data = yield getSupportRequests({ accessToken });
    yield put({
      type: "SET_SUPPORT_REQUESTS",
      payload: { items: data.items || [] }
    });
  } catch (error) {
    console.error("fetchSupportRequestsWorker", error);
    setErrorStatus(error);
    yield setRequestsLoading(false);
  }
}

// The intake response doesn't carry the real Plane workItemId, so after a
// submit we refetch the list and land on whichever ticket now has the most
// recent createdAt — the one that was just filed.
function* submitSupportRequestWorker(action) {
  try {
    yield setSubmitting(true);
    const accessToken = store.getState().auth.data.accessToken;
    yield submitSupportRequestApi({ accessToken, ...action.payload.payload });
    yield fetchSupportRequestsWorker();
    yield setSuccessStatus("Support ticket submitted");
    yield setSubmitting(false);

    const items = store.getState().support.requests.items;
    const latest = [...items].sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
    )[0];
    if (typeof action.payload.navigate === "function") {
      action.payload.navigate(latest ? `/support/${latest.workItemId}` : "/support");
    }
  } catch (error) {
    console.error("submitSupportRequestWorker", error);
    setErrorStatus(error);
    yield setSubmitting(false);
  }
}

function* fetchSupportRequestCommentsWorker(action) {
  const { workItemId } = action.payload;
  try {
    yield setCommentsLoading(workItemId, true);
    const accessToken = store.getState().auth.data.accessToken;
    const data = yield getSupportRequestComments({ accessToken, workItemId });
    yield put({
      type: "SET_SUPPORT_COMMENTS",
      payload: { workItemId, items: data.items || [] }
    });
  } catch (error) {
    console.error("fetchSupportRequestCommentsWorker", error);
    setErrorStatus(error);
    yield setCommentsLoading(workItemId, false);
  }
}

function* addSupportRequestCommentWorker(action) {
  const { workItemId, text, username } = action.payload;
  try {
    yield put({
      type: "SET_SUPPORT_COMMENT_SENDING",
      payload: { workItemId, sending: true }
    });
    const accessToken = store.getState().auth.data.accessToken;
    yield addSupportRequestCommentApi({ accessToken, workItemId, text, username });
    yield fetchSupportRequestCommentsWorker({ payload: { workItemId } });
  } catch (error) {
    console.error("addSupportRequestCommentWorker", error);
    setErrorStatus(error);
    yield put({
      type: "SET_SUPPORT_COMMENT_SENDING",
      payload: { workItemId, sending: false }
    });
  }
}

function* closeSupportRequestWorker(action) {
  const { workItemId } = action.payload;
  try {
    yield put({ type: "SET_SUPPORT_CLOSING", payload: { workItemId } });
    const accessToken = store.getState().auth.data.accessToken;
    yield closeSupportRequestApi({ accessToken, workItemId });
    yield fetchSupportRequestsWorker();
    yield setSuccessStatus("Support ticket closed");
  } catch (error) {
    console.error("closeSupportRequestWorker", error);
    setErrorStatus(error);
  } finally {
    yield put({ type: "SET_SUPPORT_CLOSING", payload: { workItemId: null } });
  }
}

function* reopenSupportRequestWorker(action) {
  const { workItemId } = action.payload;
  try {
    yield put({ type: "SET_SUPPORT_REOPENING", payload: { workItemId } });
    const accessToken = store.getState().auth.data.accessToken;
    yield reopenSupportRequestApi({ accessToken, workItemId });
    yield fetchSupportRequestsWorker();
    yield setSuccessStatus("Support ticket reopened");
  } catch (error) {
    console.error("reopenSupportRequestWorker", error);
    setErrorStatus(error);
  } finally {
    yield put({ type: "SET_SUPPORT_REOPENING", payload: { workItemId: null } });
  }
}

export default function* supportWatcher() {
  yield all([
    takeLatest(actionTypes.SUBMIT_SUPPORT_REQUEST, submitSupportRequestWorker),
    takeLatest(actionTypes.FETCH_SUPPORT_REQUESTS, fetchSupportRequestsWorker),
    takeEvery(actionTypes.FETCH_SUPPORT_REQUEST_COMMENTS, fetchSupportRequestCommentsWorker),
    takeEvery(actionTypes.ADD_SUPPORT_REQUEST_COMMENT, addSupportRequestCommentWorker),
    takeEvery(actionTypes.CLOSE_SUPPORT_REQUEST, closeSupportRequestWorker),
    takeEvery(actionTypes.REOPEN_SUPPORT_REQUEST, reopenSupportRequestWorker)
  ]);
}
